"use client";

import React, { useEffect, useState, useCallback, Fragment } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import dayjs from "@/lib/dayjs";

// Main tab options (shown as pills)
const MAIN_TABS = [{
  label: "7 days",
  value: "7d"
}, {
  label: "14 days",
  value: "14d"
}, {
  label: "1 month",
  value: "1m"
}, {
  label: "3 months",
  value: "3m"
}];

// Dropdown options (accessed via "More" button)
const MORE_OPTIONS = [{
  key: "current-week",
  label: "Current week"
}, {
  key: "last-week",
  label: "Last week"
}, {
  key: "current-month",
  label: "Current month"
}, {
  key: "last-month",
  label: "Last month"
}, {
  key: "6m",
  label: "6 months"
}, {
  key: "1y",
  label: "1 year"
}];
export default function TimeFrameLineTabs({
  children,
  accountId,
  onTabChange
}: {
  children?: React.ReactNode;
  accountId: any;
  onTabChange?: (index: number) => void;
}) {
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const [activeTabIndex, setActiveTabIndex] = useState(2); // Default to "1 month"
  const [dropdownLabel, setDropdownLabel] = useState<string | null>(null);

  // Sync tab state with URL on mount and when searchParams change
  useEffect(() => {
    const dQueryParam = searchParams.get("d");
    const defaultIndex = MAIN_TABS.findIndex(tab => tab.value === "1m");
    if (dQueryParam) {
      const tabIndex = MAIN_TABS.findIndex(tab_0 => tab_0.value === dQueryParam);
      if (tabIndex >= 0) {
        setActiveTabIndex(tabIndex);
        setDropdownLabel(null);
      } else {
        // Dropdown option selected - no active tab
        setActiveTabIndex(-1);
        // Try to match with dropdown options
        const matchedOption = MORE_OPTIONS.find(opt => {
          const days = getDaysForOption(opt.key);
          return dQueryParam === `${days}d`;
        });
        setDropdownLabel(matchedOption?.label || `${dQueryParam.replace("d", "")} days`);
      }
    } else {
      setActiveTabIndex(defaultIndex);
      setDropdownLabel(null);
    }
  }, [searchParams]);

  // Calculate days for dropdown options
  const getDaysForOption = (key: string): number => {
    switch (key) {
      case "current-week":
        return dayjs().diff(dayjs().startOf("isoWeek" as any), "days");
      case "last-week":
        return dayjs().diff(dayjs().subtract(1, "week").startOf("isoWeek" as any), "days");
      case "current-month":
        return dayjs().diff(dayjs().startOf("month"), "days");
      case "last-month":
        return dayjs().diff(dayjs().subtract(1, "month").startOf("month"), "days");
      case "6m":
        return dayjs().diff(dayjs().subtract(6, "months"), "days");
      case "1y":
        return dayjs().diff(dayjs().subtract(1, "year"), "days");
      default:
        return 30;
    }
  };
  const handleTabClick = useCallback((index: number) => {
    const tab_1 = MAIN_TABS[index];
    if (!tab_1) return;
    setActiveTabIndex(index);
    setDropdownLabel(null);
    if (onTabChange) onTabChange(index);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("d", tab_1.value);
    router.push(`${pathName}?${newSearchParams.toString()}`, undefined);
  }, [onTabChange, searchParams, pathName, router]);
  const handleDropdownSelect = useCallback((key_0: string, label: string) => {
    const days_0 = getDaysForOption(key_0);
    const newDQueryParam = `${days_0}d`;
    setActiveTabIndex(-1);
    setDropdownLabel(label);
    if (onTabChange) onTabChange(-1);
    const newSearchParams_0 = new URLSearchParams(searchParams);
    newSearchParams_0.set("d", newDQueryParam);
    router.push(`${pathName}?${newSearchParams_0.toString()}`, undefined);
  }, [onTabChange, searchParams, pathName, router]);
  const isDropdownActive = activeTabIndex === -1;
  const tabCount = MAIN_TABS.length;
  return <div className="flex items-center justify-center pt-4">
            {/* Outer card container - matches .card utility styling exactly */}
            <div className="inline-flex items-center p-1" style={{
      borderRadius: "20px",
      border: "1px solid transparent",
      background: `linear-gradient(white, white) padding-box, linear-gradient(to bottom, color-mix(in srgb, #666 4%, transparent), color-mix(in srgb, #000 12%, transparent)) border-box`,
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.02)"
    }}>
                {/* Inner track - subtle gray background for contrast */}
                <div className="inline-flex items-center rounded-[16px] bg-gray-100/60 p-1">
                    {/* Tabs section - uses CSS Grid for perfect equal widths */}
                    <div className="relative grid items-center" style={{
          gridTemplateColumns: `repeat(${tabCount}, 1fr)`
        }}>
                        {/* Animated indicator - white pill that pops against gray track */}
                        {activeTabIndex >= 0 && <span className="absolute inset-y-0 rounded-[12px] bg-white shadow-sm transition-transform duration-300 ease-out" style={{
            width: `calc(100% / ${tabCount})`,
            transform: `translateX(${activeTabIndex * 100}%)`
          }} />}

                        {/* Tab buttons */}
                        {MAIN_TABS.map((tab_2, index_0) => <button key={tab_2.value} onClick={() => handleTabClick(index_0)} className={cn("relative z-10 px-3.5 py-2 text-sm font-medium rounded-[12px] transition-colors text-center whitespace-nowrap", "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1", activeTabIndex === index_0 ? "text-gray-900" : "text-gray-500 hover:text-gray-700")}>
                                {tab_2.label}
                            </button>)}
                    </div>

                    {/* More dropdown - integrated into the track */}
                    <Menu as="div" className="relative ml-0.5">
                        <Menu.Button className={cn("inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-[12px] transition-colors whitespace-nowrap", "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1", isDropdownActive ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700")}>
                            <span>{dropdownLabel || "More"}</span>
                            <ChevronDownIcon className="h-3.5 w-3.5" />
                        </Menu.Button>

                        <Transition as={Fragment} enter="transition ease-out duration-150" enterFrom="opacity-0 scale-[0.98] translate-y-1" enterTo="opacity-100 scale-100 translate-y-0" leave="transition ease-in duration-100" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-[0.98] translate-y-1">
                            <Menu.Items className="absolute right-0 z-20 mt-2 w-40 origin-top-right rounded-xl bg-white py-1 shadow-xl shadow-gray-900/10 ring-1 ring-gray-900/5 focus:outline-none">
                                {MORE_OPTIONS.map(item => <Menu.Item key={item.key}>
                                        {({
                  active
                }) => <button className={cn("flex w-full items-center px-3.5 py-2 text-left text-[13px] font-medium transition-colors", active ? "bg-gray-50 text-gray-900" : "text-gray-600", dropdownLabel === item.label && "bg-gray-100 text-gray-900")} onClick={() => handleDropdownSelect(item.key, item.label)}>
                                                {item.label}
                                            </button>}
                                    </Menu.Item>)}
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
        </div>;
}